import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  Observable,
  Subject,
  BehaviorSubject,
  timer,
  switchMap,
  takeWhile,
  catchError,
  of,
  takeUntil,
} from "rxjs";
import { SyncProgressService } from "./sync-progress.service";
import type { SyncProgress, SyncRecord } from "./sync-progress.interface";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "../auth/auth.service";

type AnimatedCounter =
  | "displayedEmployees"
  | "displayedAliases"
  | "displayedCompleted"
  | "displayedPercentage";

@Component({
  selector: "app-sync-progress-bar",
  templateUrl: "./sync-progress-bar.component.html",
  styleUrls: ["./sync-progress-bar.component.scss"],
  standalone: false,
})
export class SyncProgressBarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() syncStateId: string | null = null;
  @Input() provider!: "GOOGLE" | "MICROSOFT" | "CALAMARI";
  @Output() onClose = new EventEmitter<void>();
  @Output() onCompleted = new EventEmitter<{ success: boolean }>();

  showDetails = false;
  showAllSyncs = false;
  loadingAllSyncs = false;
  recentSyncs: SyncRecord[] = [];
  allSyncs: SyncRecord[] = [];
  progress$: Observable<SyncProgress>;

  displayedEmployees = 0;
  displayedAliases = 0;
  displayedTotalEmployees = 0;
  displayedTotalAliases = 0;
  displayedCompleted = 0;
  displayedTotal = 0;
  displayedPercentage = 0;

  private readonly POLLING_INTERVAL = 2000;
  private destroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();
  private progressSubject = new BehaviorSubject<SyncProgress>(
    this.createPlaceholderProgress(),
  );
  private counterIntervals = new Map<
    AnimatedCounter,
    ReturnType<typeof setInterval>
  >();

  constructor(
    private syncProgressService: SyncProgressService,
    private translate: TranslateService,
    public auth: AuthService,
  ) {
    this.progress$ = this.progressSubject.asObservable();
  }

  ngOnInit(): void {
    if (this.syncStateId) {
      this.startPolling();
    } else {
      this.checkForActiveSync();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.syncStateId) {
      return;
    }

    this.stopPolling$.next();
    this.resetSyncList();
    this.clearCounterAnimations();
    this.resetDisplayedCounters();

    if (changes.syncStateId.currentValue) {
      this.startPolling();
    } else {
      this.progressSubject.next(this.createPlaceholderProgress());
      this.showDetails = false;
      this.checkForActiveSync();
    }
  }

  ngOnDestroy(): void {
    this.clearCounterAnimations();
    this.stopPolling$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
    if (!this.showDetails) {
      this.resetSyncList();
    }
  }

  toggleAllSyncs(): void {
    if (this.showAllSyncs) {
      this.showAllSyncs = false;
      return;
    }

    if (this.allSyncs.length > 0) {
      this.showAllSyncs = true;
      return;
    }

    if (!this.syncStateId || this.loadingAllSyncs) {
      return;
    }

    this.loadingAllSyncs = true;
    this.syncProgressService
      .getSyncProgress(this.syncStateId, this.provider, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fullProgress) => {
          this.allSyncs = [...fullProgress.syncs].reverse();
          this.showAllSyncs = true;
          this.loadingAllSyncs = false;
        },
        error: () => {
          this.loadingAllSyncs = false;
        },
      });
  }

  closeProgress(): void {
    this.onClose.emit();
  }

  calculateProgress(progress: SyncProgress): number {
    const completed =
      (progress.processedEmployees ?? 0) + (progress.processedAliases ?? 0);
    const total = (progress.totalEmployees || 0) + (progress.totalAliases || 0);

    if (total > 0) {
      return Math.round((completed / total) * 100);
    }

    return completed > 0 ? Math.min(95, completed * 5) : 0;
  }

  getProgressSummary(progress: SyncProgress): string {
    if (progress.status === "SAVING" && this.displayedTotal === 0) {
      return this.translate.instant("syncProgress.saving") as string;
    }
    if (progress.isPlaceholder) {
      return this.translate.instant("syncProgress.inProgress") as string;
    }
    if (progress.status === "ERROR") {
      return this.translate.instant("syncProgress.syncFailed") as string;
    }
    if (progress.status === "COMPLETED_WITH_ERROR") {
      return this.translate.instant("employee.syncCompleted") as string;
    }
    if (
      (progress.status === "IN_PROGRESS" || progress.status === "SAVING") &&
      this.displayedTotal === 0
    ) {
      return this.translate.instant("syncProgress.inProgress") as string;
    }

    return this.translate.instant("syncProgress.progress", {
      completed: this.displayedCompleted,
      total: this.displayedTotal,
      percentage: this.displayedPercentage,
    }) as string;
  }

  isCompleted(status: string): boolean {
    return (
      status === "COMPLETED_SUCCESSFULLY" ||
      status === "COMPLETED_WITH_ERROR" ||
      status === "ERROR"
    );
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  getProcessedEmployees(progress: SyncProgress): number {
    if (progress.processedEmployees !== undefined) {
      return progress.processedEmployees;
    }

    return new Set(
      progress.syncs.filter((sync) => !sync.isAlias).map((sync) => sync.email),
    ).size;
  }

  getProcessedAliases(progress: SyncProgress): number {
    if (progress.processedAliases !== undefined) {
      return progress.processedAliases;
    }

    return new Set(
      progress.syncs.filter((sync) => sync.isAlias).map((sync) => sync.email),
    ).size;
  }

  getFailedSyncs(progress: SyncProgress): SyncRecord[] {
    if (progress.failedSyncs) {
      return progress.failedSyncs;
    }

    return progress.syncs.filter((sync) => sync.error);
  }

  hasErrors(progress: SyncProgress): boolean {
    return (
      (progress.status === "COMPLETED_WITH_ERROR" ||
        progress.status === "ERROR") &&
      (!!progress.errorMessage || this.getFailedSyncs(progress).length > 0)
    );
  }

  private checkForActiveSync(attempt = 0): void {
    this.syncProgressService
      .checkActiveSyncsInBackend()
      .pipe(takeUntil(this.destroy$), takeUntil(this.stopPolling$))
      .subscribe((activeSyncs) => {
        let activeSyncStateId: string | undefined;
        switch (this.provider) {
          case "GOOGLE":
            activeSyncStateId = activeSyncs.googleSyncStateId;
            break;
          case "MICROSOFT":
            activeSyncStateId = activeSyncs.microsoftSyncStateId;
            break;
          case "CALAMARI":
            activeSyncStateId = activeSyncs.calamariSyncStateId;
            break;
        }

        if (activeSyncStateId) {
          this.syncStateId = activeSyncStateId;
          this.startPolling();
          return;
        }

        if (attempt < 6 && !this.syncStateId) {
          setTimeout(() => {
            if (!this.syncStateId) {
              this.checkForActiveSync(attempt + 1);
            }
          }, this.POLLING_INTERVAL);
        }
      });
  }

  private startPolling(): void {
    if (!this.syncStateId) {
      return;
    }

    timer(0, this.POLLING_INTERVAL)
      .pipe(
        switchMap(() =>
          this.syncProgressService
            .getSyncProgress(this.syncStateId, this.provider, true)
            .pipe(catchError(() => of(null))),
        ),
        takeWhile(
          (progress) => progress == null || !this.isCompleted(progress.status),
          true,
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
      )
      .subscribe((progress) => {
        if (!progress) {
          return;
        }

        if (this.isCompleted(progress.status)) {
          this.updateProgress(progress);
          this.onCompleted.emit({
            success: progress.status === "COMPLETED_SUCCESSFULLY",
          });
          return;
        }

        this.updateProgress(progress);
      });
  }

  private updateProgress(progress: SyncProgress): void {
    this.recentSyncs = [...progress.syncs].reverse();
    this.syncDisplayedCounters(progress);
    this.progressSubject.next(progress);
  }

  private syncDisplayedCounters(progress: SyncProgress): void {
    const employees = this.getProcessedEmployees(progress);
    const aliases = this.getProcessedAliases(progress);
    const totalEmployees = Math.max(
      progress.totalEmployees || 0,
      this.displayedTotalEmployees,
    );
    const totalAliases = Math.max(
      progress.totalAliases || 0,
      this.displayedTotalAliases,
    );
    const completed = employees + aliases;
    const total = totalEmployees + totalAliases;
    const percentage =
      total > 0
        ? Math.round((completed / total) * 100)
        : this.calculateProgress(progress);
    const animate = !this.isCompleted(progress.status);

    this.displayedTotalEmployees = totalEmployees;
    this.displayedTotalAliases = totalAliases;
    this.displayedTotal = total;

    const nextValues: Array<[AnimatedCounter, number]> = [
      ["displayedEmployees", employees],
      ["displayedAliases", aliases],
      ["displayedCompleted", completed],
      ["displayedPercentage", percentage],
    ];

    for (const [property, value] of nextValues) {
      if (this[property] === value) {
        continue;
      }
      if (animate) {
        this.animateCounter(property, this[property], value, 500);
      } else {
        this.clearCounterInterval(property);
        this[property] = value;
      }
    }
  }

  private animateCounter(
    property: AnimatedCounter,
    from: number,
    to: number,
    duration: number = 500,
  ): void {
    this.clearCounterInterval(property);
    const steps = 20;
    const stepTime = duration / steps;
    const increment = (to - from) / steps;
    let currentStep = 0;
    const intervalId = setInterval(() => {
      currentStep++;
      this[property] = Math.round(from + increment * currentStep);
      if (currentStep >= steps) {
        this.clearCounterInterval(property);
        this[property] = to;
      }
    }, stepTime);
    this.counterIntervals.set(property, intervalId);
  }

  private clearCounterInterval(property: AnimatedCounter): void {
    const intervalId = this.counterIntervals.get(property);
    if (intervalId) {
      clearInterval(intervalId);
      this.counterIntervals.delete(property);
    }
  }

  private clearCounterAnimations(): void {
    for (const intervalId of this.counterIntervals.values()) {
      clearInterval(intervalId);
    }
    this.counterIntervals.clear();
  }

  private resetDisplayedCounters(): void {
    this.displayedEmployees = 0;
    this.displayedAliases = 0;
    this.displayedTotalEmployees = 0;
    this.displayedTotalAliases = 0;
    this.displayedCompleted = 0;
    this.displayedTotal = 0;
    this.displayedPercentage = 0;
  }

  private resetSyncList(): void {
    this.showAllSyncs = false;
    this.loadingAllSyncs = false;
    this.allSyncs = [];
    this.recentSyncs = [];
  }

  private createPlaceholderProgress(): SyncProgress {
    return {
      isPlaceholder: true,
      domain: "",
      provider: this.provider || "GOOGLE",
      status: "IN_PROGRESS",
      syncs: [],
      totalEmployees: 0,
      totalAliases: 0,
    };
  }
}
